import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch } from "../apiBase";

// ============================================
// ANALYTICS PROVIDERS CONFIGURATION
// ============================================

interface AnalyticsProvider {
  name: string;
  init: (config: ProviderConfig) => void;
  track: (eventName: string, properties?: Record<string, any>) => void;
  identify: (userId: string, traits?: Record<string, any>) => void;
  page: (pageName: string, properties?: Record<string, any>) => void;
  revenue: (amount: number, productId: string, currency?: string) => void;
}

interface ProviderConfig {
  apiKey?: string;
  debug?: boolean;
  serverUrl?: string;
}

// Amplitude Provider
const amplitudeProvider: AnalyticsProvider = {
  name: 'amplitude',
  init: (config) => {
    if (typeof window === 'undefined') return;
    
    const apiKey = config.apiKey || import.meta.env.VITE_AMPLITUDE_API_KEY;
    if (!apiKey) {
      if (config.debug) console.log('[Amplitude] No API key provided, skipping');
      return;
    }
    
    // Load Amplitude SDK dynamically
    if (!(window as any).amplitude) {
      // Load Amplitude via script tag (official snippet - ts-ignore for 'this' usage)
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://cdn.amplitude.com/libs/amplitude-8.21.4-min.gz.js';
      script.onload = () => {
        if ((window as any).amplitude) {
          (window as any).amplitude.init(apiKey, undefined, {
            includeReferrer: true,
            includeUtm: true,
            includeGclid: true,
            saveEvents: true,
            batchEvents: true,
            eventUploadThreshold: 10,
            eventUploadPeriodMillis: 30000,
          });
          if (config.debug) console.log('[Amplitude] SDK loaded and initialized');
        }
      };
      document.head.appendChild(script);
      
      // Setup stub while loading
      (window as any).amplitude = {
        _q: [],
        init: function() { this._q.push(['init'].concat(Array.prototype.slice.call(arguments))); },
        logEvent: function() { this._q.push(['logEvent'].concat(Array.prototype.slice.call(arguments))); },
        setUserId: function() { this._q.push(['setUserId'].concat(Array.prototype.slice.call(arguments))); },
        setUserProperties: function() { this._q.push(['setUserProperties'].concat(Array.prototype.slice.call(arguments))); },
        logRevenueV2: function() { this._q.push(['logRevenueV2'].concat(Array.prototype.slice.call(arguments))); },
        Revenue: function() { return { setProductId: () => this, setPrice: () => this, setRevenueType: () => this }; },
      };
    } else {
      (window as any).amplitude.init(apiKey, undefined, {
        includeReferrer: true,
        includeUtm: true,
        includeGclid: true,
        saveEvents: true,
        batchEvents: true,
        eventUploadThreshold: 10,
        eventUploadPeriodMillis: 30000,
      });
    }
    
    if (config.debug) console.log('[Amplitude] Initialized');
  },
  track: (eventName, properties) => {
    if (typeof window === 'undefined' || !(window as any).amplitude) return;
    (window as any).amplitude.logEvent(eventName, properties);
  },
  identify: (userId, traits) => {
    if (typeof window === 'undefined' || !(window as any).amplitude) return;
    (window as any).amplitude.setUserId(userId);
    if (traits) {
      (window as any).amplitude.setUserProperties(traits);
    }
  },
  page: (pageName, properties) => {
    if (typeof window === 'undefined' || !(window as any).amplitude) return;
    (window as any).amplitude.logEvent('Page View', { page: pageName, ...properties });
  },
  revenue: (amount, productId, currency = 'BRL') => {
    if (typeof window === 'undefined' || !(window as any).amplitude) return;
    const revenue = new (window as any).amplitude.Revenue()
      .setProductId(productId)
      .setPrice(amount)
      .setRevenueType('purchase');
    (window as any).amplitude.logRevenueV2(revenue);
  },
};

// Google Analytics 4 Provider
const ga4Provider: AnalyticsProvider = {
  name: 'ga4',
  init: (config) => {
    if (typeof window === 'undefined') return;
    
    const measurementId = config.apiKey || import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) {
      if (config.debug) console.log('[GA4] No Measurement ID provided, skipping');
      return;
    }
    
    // Load gtag.js
    if (!(window as any).gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
      
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).gtag = function() {
        (window as any).dataLayer.push(arguments);
      };
      (window as any).gtag('js', new Date());
      (window as any).gtag('config', measurementId, {
        send_page_view: false, // Manual page tracking
      });
    }
    
    if (config.debug) console.log('[GA4] Initialized');
  },
  track: (eventName, properties) => {
    if (typeof window === 'undefined' || !(window as any).gtag) return;
    (window as any).gtag('event', eventName, properties);
  },
  identify: (userId, traits) => {
    if (typeof window === 'undefined' || !(window as any).gtag) return;
    (window as any).gtag('set', { user_id: userId });
    if (traits) {
      (window as any).gtag('set', 'user_properties', traits);
    }
  },
  page: (pageName, properties) => {
    if (typeof window === 'undefined' || !(window as any).gtag) return;
    (window as any).gtag('event', 'page_view', { page_title: pageName, ...properties });
  },
  revenue: (amount, productId, currency = 'BRL') => {
    if (typeof window === 'undefined' || !(window as any).gtag) return;
    (window as any).gtag('event', 'purchase', {
      value: amount,
      currency,
      items: [{ item_id: productId, price: amount }],
    });
  },
};

// Server Analytics Provider (for our own backend)
const serverProvider: AnalyticsProvider = {
  name: 'server',
  init: (config) => {
    if (config.debug) console.log('[Server Analytics] Initialized');
  },
  track: async (eventName, properties) => {
    try {
      await apiFetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: eventName, properties, timestamp: Date.now() }),
      });
    } catch (e) {
      // Silently fail
    }
  },
  identify: async (userId, traits) => {
    try {
      await apiFetch('/api/analytics/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, traits, timestamp: Date.now() }),
      });
    } catch (e) {
      // Silently fail
    }
  },
  page: async (pageName, properties) => {
    try {
      await apiFetch('/api/analytics/page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: pageName, properties, timestamp: Date.now() }),
      });
    } catch (e) {
      // Silently fail
    }
  },
  revenue: async (amount, productId, currency = 'BRL') => {
    try {
      await apiFetch('/api/analytics/revenue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, productId, currency, timestamp: Date.now() }),
      });
    } catch (e) {
      // Silently fail
    }
  },
};

// Active providers (will be initialized)
let activeProviders: AnalyticsProvider[] = [];
let analyticsInitialized = false;

// Initialize all analytics providers
export const initializeAnalytics = (debug = false) => {
  if (analyticsInitialized) return;
  
  const config: ProviderConfig = { debug };
  
  // Initialize providers
  activeProviders = [amplitudeProvider, ga4Provider, serverProvider];
  activeProviders.forEach(provider => provider.init(config));
  
  analyticsInitialized = true;
  
  if (debug) console.log('[Analytics] All providers initialized');
};

// Send to all providers
const trackToAll = (eventName: string, properties?: Record<string, any>) => {
  activeProviders.forEach(provider => provider.track(eventName, properties));
};

const identifyToAll = (userId: string, traits?: Record<string, any>) => {
  activeProviders.forEach(provider => provider.identify(userId, traits));
};

const pageToAll = (pageName: string, properties?: Record<string, any>) => {
  activeProviders.forEach(provider => provider.page(pageName, properties));
};

const revenueToAll = (amount: number, productId: string, currency?: string) => {
  activeProviders.forEach(provider => provider.revenue(amount, productId, currency));
};

// ============================================
// TYPES
// ============================================

// Tipos de eventos
export type AnalyticsEventCategory = 
  | 'gameplay'
  | 'monetization'
  | 'engagement'
  | 'social'
  | 'progression'
  | 'technical'
  | 'ab_test';

export interface AnalyticsEvent {
  id: string;
  category: AnalyticsEventCategory;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
  timestamp: number;
  sessionId: string;
  userId?: string;
}

// Sessão do usuário
export interface UserSession {
  id: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  events: number;
  screens: string[];
  purchases: number;
  purchaseValue: number;
}

// Métricas agregadas
export interface AggregatedMetrics {
  // Engajamento
  totalSessions: number;
  averageSessionDuration: number;
  totalPlayTime: number;
  dau: number;
  mau: number;
  dauMauRatio: number;
  
  // Retenção
  d1Retention: number;
  d7Retention: number;
  d30Retention: number;
  
  // Monetização
  totalRevenue: number;
  arpdau: number;
  arppu: number;
  payingUsers: number;
  conversionRate: number;
  ltv: number;
  
  // Gameplay
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
  averageWaveReached: number;
  bossesDefeated: number;
  
  // Aquisição
  newUsers: number;
  organicInstalls: number;
  referralInstalls: number;
}

// Funil de conversão
export interface FunnelStep {
  name: string;
  count: number;
  conversionRate: number;
}

export interface Funnel {
  id: string;
  name: string;
  steps: FunnelStep[];
  overallConversion: number;
}

// Predefinição de funis comuns
export const PREDEFINED_FUNNELS = {
  firstPurchase: {
    id: 'first_purchase',
    name: 'Primeira Compra',
    steps: ['app_open', 'tutorial_complete', 'shop_view', 'item_view', 'purchase_start', 'purchase_complete'],
  },
  battlePassPurchase: {
    id: 'battle_pass',
    name: 'Battle Pass',
    steps: ['menu_open', 'battle_pass_view', 'premium_view', 'purchase_start', 'purchase_complete'],
  },
  matchCompletion: {
    id: 'match_completion',
    name: 'Completar Partida',
    steps: ['menu_open', 'play_click', 'match_start', 'wave_1_complete', 'match_complete'],
  },
  socialEngagement: {
    id: 'social',
    name: 'Engajamento Social',
    steps: ['profile_view', 'friend_add', 'multiplayer_click', 'room_join', 'multiplayer_complete'],
  },
};

// A/B Test
export interface ABTest {
  id: string;
  name: string;
  description: string;
  variants: ABTestVariant[];
  startDate: number;
  endDate?: number;
  status: 'draft' | 'running' | 'paused' | 'completed';
  targetMetric: string;
  minimumSampleSize: number;
  currentSampleSize: number;
  winner?: string;
}

export interface ABTestVariant {
  id: string;
  name: string;
  weight: number; // Porcentagem de usuários (0-100)
  participants: number;
  conversions: number;
  conversionRate: number;
  revenue: number;
  averageValue: number;
}

// Eventos predefinidos
export const ANALYTICS_EVENTS = {
  // Gameplay
  GAME_START: 'game_start',
  GAME_END: 'game_end',
  WAVE_COMPLETE: 'wave_complete',
  BOSS_ENCOUNTER: 'boss_encounter',
  BOSS_DEFEAT: 'boss_defeat',
  PLAYER_DEATH: 'player_death',
  POWER_UP_COLLECT: 'power_up_collect',
  
  // Monetização
  SHOP_VIEW: 'shop_view',
  ITEM_VIEW: 'item_view',
  PURCHASE_START: 'purchase_start',
  PURCHASE_COMPLETE: 'purchase_complete',
  PURCHASE_FAIL: 'purchase_fail',
  AD_VIEW_START: 'ad_view_start',
  AD_VIEW_COMPLETE: 'ad_view_complete',
  
  // Engajamento
  APP_OPEN: 'app_open',
  APP_CLOSE: 'app_close',
  SCREEN_VIEW: 'screen_view',
  BUTTON_CLICK: 'button_click',
  TUTORIAL_START: 'tutorial_start',
  TUTORIAL_COMPLETE: 'tutorial_complete',
  
  // Social
  FRIEND_ADD: 'friend_add',
  MULTIPLAYER_JOIN: 'multiplayer_join',
  MULTIPLAYER_LEAVE: 'multiplayer_leave',
  SHARE_CLICK: 'share_click',
  
  // Progressão
  LEVEL_UP: 'level_up',
  ACHIEVEMENT_UNLOCK: 'achievement_unlock',
  BATTLE_PASS_LEVEL: 'battle_pass_level',
  DAILY_REWARD_CLAIM: 'daily_reward_claim',
  
  // A/B Testing
  AB_TEST_EXPOSURE: 'ab_test_exposure',
  AB_TEST_CONVERSION: 'ab_test_conversion',
};

interface AnalyticsState {
  // Sessão atual
  currentSession: UserSession | null;
  sessionHistory: UserSession[];
  
  // Eventos
  events: AnalyticsEvent[];
  eventQueue: AnalyticsEvent[]; // Fila para envio em batch
  
  // Métricas
  metrics: AggregatedMetrics;
  
  // Funis
  funnels: Record<string, Funnel>;
  funnelProgress: Record<string, number>; // Progresso do usuário em cada funil
  
  // A/B Tests
  activeTests: ABTest[];
  userVariants: Record<string, string>; // testId -> variantId
  
  // Configuração
  userId: string | null;
  isEnabled: boolean;
  debugMode: boolean;
  batchSize: number;
  flushInterval: number;
  
  // Ações
  initSession: (userId?: string) => void;
  endSession: () => void;
  
  // Tracking
  trackEvent: (category: AnalyticsEventCategory, action: string, label?: string, value?: number, metadata?: Record<string, any>) => void;
  trackScreenView: (screenName: string) => void;
  trackPurchase: (itemId: string, value: number, currency: string) => void;
  trackGameplay: (action: string, metadata?: Record<string, any>) => void;
  
  // Funis
  trackFunnelStep: (funnelId: string, step: string) => void;
  getFunnelConversion: (funnelId: string) => number;
  
  // A/B Testing
  getVariant: (testId: string) => string | null;
  assignVariant: (testId: string) => string;
  trackABConversion: (testId: string, value?: number) => void;
  
  // Métricas
  calculateMetrics: () => void;
  getMetric: (metricName: keyof AggregatedMetrics) => number;
  
  // Batch & Sync
  flushEvents: () => Promise<void>;
  
  // Debug
  setDebugMode: (enabled: boolean) => void;
  exportData: () => string;
}

const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useAnalytics = create<AnalyticsState>()(
  persist(
    (set, get) => ({
      currentSession: null,
      sessionHistory: [],
      events: [],
      eventQueue: [],
      metrics: {
        totalSessions: 0,
        averageSessionDuration: 0,
        totalPlayTime: 0,
        dau: 0,
        mau: 0,
        dauMauRatio: 0,
        d1Retention: 0,
        d7Retention: 0,
        d30Retention: 0,
        totalRevenue: 0,
        arpdau: 0,
        arppu: 0,
        payingUsers: 0,
        conversionRate: 0,
        ltv: 0,
        gamesPlayed: 0,
        gamesWon: 0,
        winRate: 0,
        averageWaveReached: 0,
        bossesDefeated: 0,
        newUsers: 0,
        organicInstalls: 0,
        referralInstalls: 0,
      },
      funnels: {},
      funnelProgress: {},
      activeTests: [],
      userVariants: {},
      userId: null,
      isEnabled: true,
      debugMode: false,
      batchSize: 10,
      flushInterval: 30000, // 30 segundos
      
      initSession: (userId?: string) => {
        // Initialize analytics providers on first session
        initializeAnalytics(get().debugMode);
        
        const session: UserSession = {
          id: generateSessionId(),
          startTime: Date.now(),
          events: 0,
          screens: [],
          purchases: 0,
          purchaseValue: 0,
        };
        
        // Identify user in all providers
        if (userId) {
          identifyToAll(userId, {
            firstSeen: get().sessionHistory.length === 0 ? Date.now() : undefined,
            sessionCount: get().sessionHistory.length + 1,
          });
        }
        
        set(state => ({
          currentSession: session,
          userId: userId || state.userId,
        }));
        
        // Track app open to all providers
        trackToAll('session_start', { sessionId: session.id });
        get().trackEvent('engagement', ANALYTICS_EVENTS.APP_OPEN);
        
        if (get().debugMode) {
          console.log('[Analytics] Session started:', session.id);
        }
      },
      
      endSession: () => {
        const state = get();
        if (!state.currentSession) return;
        
        const endTime = Date.now();
        const duration = endTime - state.currentSession.startTime;
        
        const completedSession: UserSession = {
          ...state.currentSession,
          endTime,
          duration,
        };
        
        // Track app close
        state.trackEvent('engagement', ANALYTICS_EVENTS.APP_CLOSE, undefined, duration);
        
        set(state => ({
          currentSession: null,
          sessionHistory: [...state.sessionHistory.slice(-99), completedSession],
        }));
        
        // Flush remaining events
        state.flushEvents();
        
        if (state.debugMode) {
          console.log('[Analytics] Session ended:', completedSession);
        }
      },
      
      trackEvent: (category, action, label, value, metadata) => {
        const state = get();
        if (!state.isEnabled) return;
        
        const event: AnalyticsEvent = {
          id: generateId(),
          category,
          action,
          label,
          value,
          metadata,
          timestamp: Date.now(),
          sessionId: state.currentSession?.id || 'no_session',
          userId: state.userId || undefined,
        };
        
        // Send to all analytics providers
        const eventName = `${category}_${action}`;
        trackToAll(eventName, {
          category,
          action,
          label,
          value,
          ...metadata,
          sessionId: event.sessionId,
          userId: event.userId,
        });
        
        set(state => ({
          events: [...state.events.slice(-999), event], // Manter últimos 1000 eventos
          eventQueue: [...state.eventQueue, event],
          currentSession: state.currentSession ? {
            ...state.currentSession,
            events: state.currentSession.events + 1,
          } : null,
        }));
        
        // Auto-flush se atingir batch size
        if (get().eventQueue.length >= state.batchSize) {
          get().flushEvents();
        }
        
        if (state.debugMode) {
          console.log('[Analytics] Event:', { category, action, label, value, metadata });
        }
      },
      
      trackScreenView: (screenName) => {
        const state = get();
        
        // Send to all providers
        pageToAll(screenName);
        
        state.trackEvent('engagement', ANALYTICS_EVENTS.SCREEN_VIEW, screenName);
        
        if (state.currentSession) {
          set(state => ({
            currentSession: state.currentSession ? {
              ...state.currentSession,
              screens: Array.from(new Set([...state.currentSession.screens, screenName])),
            } : null,
          }));
        }
      },
      
      trackPurchase: (itemId, value, currency) => {
        const state = get();
        
        // Send revenue to all providers
        revenueToAll(value, itemId, currency);
        
        state.trackEvent('monetization', ANALYTICS_EVENTS.PURCHASE_COMPLETE, itemId, value, { currency });
        
        if (state.currentSession) {
          set(state => ({
            currentSession: state.currentSession ? {
              ...state.currentSession,
              purchases: state.currentSession.purchases + 1,
              purchaseValue: state.currentSession.purchaseValue + value,
            } : null,
          }));
        }
      },
      
      trackGameplay: (action, metadata) => {
        get().trackEvent('gameplay', action, undefined, undefined, metadata);
      },
      
      trackFunnelStep: (funnelId, step) => {
        const state = get();
        const funnelConfig = PREDEFINED_FUNNELS[funnelId as keyof typeof PREDEFINED_FUNNELS];
        
        if (!funnelConfig) return;
        
        const stepIndex = funnelConfig.steps.indexOf(step);
        if (stepIndex === -1) return;
        
        const currentProgress = state.funnelProgress[funnelId] || -1;
        
        // Só avançar se for o próximo passo
        if (stepIndex === currentProgress + 1) {
          set(state => ({
            funnelProgress: {
              ...state.funnelProgress,
              [funnelId]: stepIndex,
            },
          }));
          
          state.trackEvent('engagement', `funnel_${funnelId}_step`, step, stepIndex);
        }
      },
      
      getFunnelConversion: (funnelId) => {
        const state = get();
        const funnelConfig = PREDEFINED_FUNNELS[funnelId as keyof typeof PREDEFINED_FUNNELS];
        
        if (!funnelConfig) return 0;
        
        const progress = state.funnelProgress[funnelId] || 0;
        return (progress / (funnelConfig.steps.length - 1)) * 100;
      },
      
      getVariant: (testId) => {
        return get().userVariants[testId] || null;
      },
      
      assignVariant: (testId) => {
        const state = get();
        
        // Se já tem variante, retornar
        const existing = state.userVariants[testId];
        if (existing) return existing;
        
        // Encontrar teste
        const test = state.activeTests.find(t => t.id === testId);
        if (!test || test.status !== 'running') {
          return 'control';
        }
        
        // Atribuir variante baseado em peso
        const random = Math.random() * 100;
        let cumulative = 0;
        let selectedVariant = test.variants[0].id;
        
        for (const variant of test.variants) {
          cumulative += variant.weight;
          if (random <= cumulative) {
            selectedVariant = variant.id;
            break;
          }
        }
        
        set(state => ({
          userVariants: {
            ...state.userVariants,
            [testId]: selectedVariant,
          },
        }));
        
        // Track exposure
        state.trackEvent('ab_test', ANALYTICS_EVENTS.AB_TEST_EXPOSURE, testId, undefined, {
          variant: selectedVariant,
        });
        
        return selectedVariant;
      },
      
      trackABConversion: (testId, value) => {
        const state = get();
        const variant = state.userVariants[testId];
        
        if (!variant) return;
        
        state.trackEvent('ab_test', ANALYTICS_EVENTS.AB_TEST_CONVERSION, testId, value, {
          variant,
        });
      },
      
      calculateMetrics: () => {
        const state = get();
        const sessions = state.sessionHistory;
        const events = state.events;
        
        // Calcular métricas básicas
        const totalSessions = sessions.length;
        const totalPlayTime = sessions.reduce((acc, s) => acc + (s.duration || 0), 0);
        const averageSessionDuration = totalSessions > 0 ? totalPlayTime / totalSessions : 0;
        
        // Monetização
        const purchaseEvents = events.filter(e => e.action === ANALYTICS_EVENTS.PURCHASE_COMPLETE);
        const totalRevenue = purchaseEvents.reduce((acc, e) => acc + (e.value || 0), 0);
        const payingUsers = new Set(purchaseEvents.map(e => e.userId)).size;
        const uniqueUsers = new Set(sessions.map(s => s.id)).size;
        const conversionRate = uniqueUsers > 0 ? (payingUsers / uniqueUsers) * 100 : 0;
        const arppu = payingUsers > 0 ? totalRevenue / payingUsers : 0;
        
        // Gameplay
        const gameEndEvents = events.filter(e => e.action === ANALYTICS_EVENTS.GAME_END);
        const gamesPlayed = gameEndEvents.length;
        const gamesWon = gameEndEvents.filter(e => e.metadata?.won).length;
        const winRate = gamesPlayed > 0 ? (gamesWon / gamesPlayed) * 100 : 0;
        const bossesDefeated = events.filter(e => e.action === ANALYTICS_EVENTS.BOSS_DEFEAT).length;
        
        set({
          metrics: {
            ...state.metrics,
            totalSessions,
            averageSessionDuration,
            totalPlayTime,
            totalRevenue,
            payingUsers,
            conversionRate,
            arppu,
            gamesPlayed,
            gamesWon,
            winRate,
            bossesDefeated,
          },
        });
      },
      
      getMetric: (metricName) => {
        return get().metrics[metricName];
      },
      
      flushEvents: async () => {
        const state = get();
        if (state.eventQueue.length === 0) return;
        
        const eventsToFlush = [...state.eventQueue];
        
        // Limpar fila antes de enviar
        set({ eventQueue: [] });
        
        try {
          // Enviar eventos para o servidor de analytics
          await apiFetch('/api/analytics/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(eventsToFlush),
          });
          
          if (state.debugMode) {
            console.log('[Analytics] Flushed events:', eventsToFlush.length);
          }
        } catch (error) {
          // Re-adicionar eventos à fila em caso de erro
          set(state => ({
            eventQueue: [...eventsToFlush, ...state.eventQueue],
          }));
          
          if (state.debugMode) {
            console.error('[Analytics] Flush failed:', error);
          }
        }
      },
      
      setDebugMode: (enabled) => {
        set({ debugMode: enabled });
      },
      
      exportData: () => {
        const state = get();
        return JSON.stringify({
          sessions: state.sessionHistory,
          events: state.events,
          metrics: state.metrics,
          funnels: state.funnelProgress,
          variants: state.userVariants,
        }, null, 2);
      },
    }),
    {
      name: 'rocketanks-analytics',
      partialize: (state) => ({
        sessionHistory: state.sessionHistory.slice(-50),
        metrics: state.metrics,
        funnelProgress: state.funnelProgress,
        userVariants: state.userVariants,
        userId: state.userId,
      }),
    }
  )
);

// Hook para A/B Testing simplificado
export const useABTest = (testId: string) => {
  const { getVariant, assignVariant, trackABConversion } = useAnalytics();
  
  const variant = getVariant(testId) || assignVariant(testId);
  
  const trackConversion = (value?: number) => {
    trackABConversion(testId, value);
  };
  
  return {
    variant,
    isControl: variant === 'control',
    isVariantA: variant === 'variant_a',
    isVariantB: variant === 'variant_b',
    trackConversion,
  };
};

// Hook para tracking de gameplay
export const useGameplayAnalytics = () => {
  const { trackGameplay, trackEvent } = useAnalytics();
  
  return {
    trackGameStart: (mode: string) => trackGameplay(ANALYTICS_EVENTS.GAME_START, { mode }),
    trackGameEnd: (won: boolean, wave: number, score: number) => trackGameplay(ANALYTICS_EVENTS.GAME_END, { won, wave, score }),
    trackWaveComplete: (wave: number) => trackGameplay(ANALYTICS_EVENTS.WAVE_COMPLETE, { wave }),
    trackBossEncounter: (bossId: string) => trackGameplay(ANALYTICS_EVENTS.BOSS_ENCOUNTER, { bossId }),
    trackBossDefeat: (bossId: string, time: number) => trackGameplay(ANALYTICS_EVENTS.BOSS_DEFEAT, { bossId, time }),
    trackPlayerDeath: (wave: number, cause: string) => trackGameplay(ANALYTICS_EVENTS.PLAYER_DEATH, { wave, cause }),
    trackPowerUpCollect: (type: string) => trackGameplay(ANALYTICS_EVENTS.POWER_UP_COLLECT, { type }),
  };
};
